import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { copyToClipboard } from '../utils/clipboard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Monitor, Activity, RefreshCw, Loader, ExternalLink, Copy, Clock, Users } from 'lucide-react';
import { Label } from '../components/ui/label';
import screenconnectService from '../services/screenconnectService';
import { toast } from 'sonner';
import { formatInCST } from '../utils/timezone';

export default function ScreenConnectDashboard() {
  const navigate = useNavigate();
  const [activeSessions, setActiveSessions] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [healthStatus, setHealthStatus] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [showWebhookSetup, setShowWebhookSetup] = useState(false);

  useEffect(() => {
    loadData();
    checkHealth();
    loadWebhookUrl();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadWebhookUrl = async () => {
    try {
      const result = await screenconnectService.getWebhookUrl();
      if (result && result.webhook_url) {
        setWebhookUrl(result.webhook_url);
      }
    } catch (error) {
      console.error('Error loading webhook URL:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [sessionsResult, machinesResult, webhookSessions] = await Promise.all([
        screenconnectService.getActiveSessions(),
        screenconnectService.getMachines(),
        screenconnectService.getWebhookSessions()
      ]);
      
      if (sessionsResult && sessionsResult.sessions) {
        setActiveSessions(sessionsResult.sessions);
      }
      
      // Merge webhook sessions with manual sessions
      if (webhookSessions && webhookSessions.sessions) {
        const combined = [...(sessionsResult?.sessions || []), ...webhookSessions.sessions];
        setActiveSessions(combined);
      }
      
      if (machinesResult && machinesResult.machines) {
        setMachines(machinesResult.machines);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load ScreenConnect data');
    } finally {
      setLoading(false);
    }
  };

  const checkHealth = async () => {
    try {
      const result = await screenconnectService.checkHealth();
      setHealthStatus(result);
    } catch (error) {
      console.error('Error checking health:', error);
      setHealthStatus({ status: 'unhealthy', screenconnect_connected: false });
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    await checkHealth();
    setRefreshing(false);
    toast.success('Sessions refreshed');
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await screenconnectService.syncMachines();
      if (result.success) {
        toast.success('Machines synced successfully');
        await loadData();
      }
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Failed to sync machines');
    } finally {
      setSyncing(false);
    }
  };

  const handleCopyLink = async (url) => {
    const success = await copyToClipboard(url);
    if (success) {
      toast.success('Link copied to clipboard');
    } else {
      toast.error('Copy failed. Link: ' + url.substring(0, 50) + '...');
    }
  };

  const handleJoinSession = (url) => {
// removed console statement
    if (!url || url.includes('undefined') || url.includes('null')) {
      toast.error('Invalid session URL');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.info('Opening ScreenConnect session...');
  };

  const stats = {
    total: activeSessions.length,
    active: activeSessions.filter(s => s.session_id).length,
    machines: machines.length,
    linked: machines.filter(m => m.asset_id).length,
  };

  return (
    <div className="space-y-6 w-full p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Monitor className="w-8 h-8" />
            ScreenConnect Remote Sessions
          </h1>
          <p className="text-gray-600 mt-1">Manage active remote support sessions across your organization</p>
        </div>
        <div className="flex gap-2 items-center">
          {healthStatus && (
            <Badge 
              variant={healthStatus.screenconnect_connected ? 'default' : 'destructive'}
              className={healthStatus.screenconnect_connected ? 'bg-green-100 text-green-800' : ''}
            >
              {healthStatus.screenconnect_connected ? 'Connected' : 'Disconnected'}
            </Badge>
          )}
          <Button 
            onClick={handleSync}
            disabled={syncing}
            variant="default"
          >
            {syncing ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Sync Machines
              </>
            )}
          </Button>
          <Button 
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
          >
            {refreshing ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => window.location.href = '/assets?has_screenconnect=true'}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Machines</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{stats.machines}</p>
            <p className="text-xs text-gray-500 mt-1">Click to view all</p>
          </CardContent>
        </Card>

        <Card 
          className="border-green-200 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => window.location.href = '/assets?has_screenconnect=true'}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-600">Linked to Assets</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600">{stats.linked}</p>
            <p className="text-xs text-gray-500 mt-1">Click to view linked</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => {
            // Scroll to active sessions table
            document.querySelector('[data-testid="active-sessions-card"]')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Active Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{stats.active}</p>
            <p className="text-xs text-gray-500 mt-1">Click to view sessions</p>
          </CardContent>
        </Card>

        <Card 
          className="border-blue-200 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => {
            document.querySelector('[data-testid="active-sessions-card"]')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-blue-600 flex items-center gap-2">
              <Monitor className="w-4 h-4" />
              Ready to Connect
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-blue-600">{stats.active}</p>
            <p className="text-xs text-gray-500 mt-1">Click to view sessions</p>
          </CardContent>
        </Card>

        <Card className="border-green-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-600">Instance Status</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge className="bg-green-100 text-green-800">
              {healthStatus?.screenconnect_connected ? 'Online' : 'Checking...'}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Active Sessions Table */}
      <Card data-testid="active-sessions-card">
        <CardHeader>
          <CardTitle>Active Remote Sessions</CardTitle>
          <CardDescription>Currently active ScreenConnect sessions across all assets</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin" />
            </div>
          ) : activeSessions.length === 0 ? (
            <div className="text-center py-8">
              <Monitor className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500 mb-4">No active remote sessions</p>
              <p className="text-sm text-gray-400">
                Launch remote support from any asset's Integrations tab to create a session
              </p>
            </div>
          ) : (
            <div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Asset Name</TableHead>
                    <TableHead>Session ID</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeSessions.map((session) => (
                    <TableRow key={session.session_id} data-testid="active-session-row">
                      <TableCell className="font-semibold">
                        <button
                          className="text-blue-600 hover:underline flex items-center gap-1"
                          onClick={() => navigate(`/assets?search=${session.asset_name}`)}
                        >
                          <Monitor className="h-4 w-4" />
                          {session.asset_name}
                        </button>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {session.session_id}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {session.created_at ? formatInCST(session.created_at, 'MMM d, h:mm a') : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-200 transition-colors">
                          ACTIVE
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="default"
                            onClick={() => handleJoinSession(session.host_url || screenconnectService.getHostJoinUrl(session.session_id))}
                            data-testid="join-session-btn"
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Join
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleCopyLink(session.guest_url || screenconnectService.getGuestJoinUrl(session.session_id))}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Webhook Setup Card */}
      <Card className="bg-green-50 border-green-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Activity className="h-5 w-5 text-green-600 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-green-900">Webhook Integration (Real-Time Sync)</h4>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowWebhookSetup(!showWebhookSetup)}
                  className="border-green-300"
                >
                  {showWebhookSetup ? 'Hide' : 'Show'} Setup Instructions
                </Button>
              </div>
              
              {showWebhookSetup && (
                <div className="space-y-3 mt-3">
                  <div className="bg-white p-3 rounded border border-green-200">
                    <Label className="text-xs text-gray-600 font-semibold">Webhook URL:</Label>
                    <div className="flex gap-2 mt-1">
                      <code className="text-xs bg-gray-100 p-2 rounded flex-1 break-all">
                        {webhookUrl || 'Loading...'}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const textarea = document.createElement('textarea');
                          textarea.value = webhookUrl;
                          textarea.style.position = 'fixed';
                          textarea.style.opacity = '0';
                          document.body.appendChild(textarea);
                          textarea.select();
                          try {
                            document.execCommand('copy');
                            toast.success('Webhook URL copied!');
                          } catch (err) {
                            toast.error('Failed to copy');
                          }
                          document.body.removeChild(textarea);
                        }}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="text-sm text-green-800">
                    <p className="font-semibold mb-2">Setup Instructions:</p>
                    <ol className="ml-4 list-decimal space-y-1">
                      <li>Log into ScreenConnect admin panel</li>
                      <li>Go to <strong>Administration → Extensions</strong></li>
                      <li>Click <strong>Add Extension</strong> or configure existing webhook</li>
                      <li>Paste the webhook URL above</li>
                      <li>Enable events: <strong>SessionStart, SessionEnd, ConnectionStart, ConnectionEnd</strong></li>
                      <li>Save and test the webhook</li>
                      <li>Sessions will now sync automatically to CipherOne</li>
                    </ol>
                  </div>
                </div>
              )}
              
              {!showWebhookSetup && (
                <p className="text-sm text-green-800 mt-1">
                  Configure webhooks in ScreenConnect to enable real-time session tracking without API polling.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Activity className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">How to Launch Remote Support</h4>
              <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
                <li>Navigate to any asset in the Assets page</li>
                <li>Open the asset detail dialog or full page view</li>
                <li>Click the <strong>Integrations</strong> tab</li>
                <li>Find the <strong>ScreenConnect</strong> section</li>
                <li>Click <strong>"Launch Remote Support"</strong></li>
                <li>The ScreenConnect window will open automatically with the remote session</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
